import apiClient from './apiClient';

export interface InventoryMovementItem {
  id: string;
  projectId?: string | null;
  resourceId: string;
  storageLocationId: string;
  storageZoneId?: string | null;
  taskId?: string | null;
  movementType: string;
  sourceType?: string | null;
  quantity: number;
  unitCost?: number | null;
  reasonCode?: string | null;
  notes?: string | null;
  metadata?: Record<string, unknown> | null;
  createdAt?: string;
  createdById?: string | null;
}

export interface CreateInventoryMovementPayload {
  projectId?: string;
  resourceId: string;
  storageLocationId: string;
  storageZoneId?: string | null;
  taskId?: string;
  movementType:
    | 'receipt'
    | 'issue'
    | 'return'
    | 'transfer_out'
    | 'transfer_in'
    | 'adjustment'
    | 'damage'
    | 'theft'
    | 'write_off';
  sourceType?: 'manual' | 'task' | 'transfer' | 'audit' | 'grn_item';
  quantity: number;
  unitCost?: number;
  batchLotNumber?: string;
  referenceType?: string;
  referenceId?: string;
  reasonCode?: string;
  notes?: string;
  metadata?: Record<string, unknown>;
}

export interface CreateInventoryAdjustmentPayload {
  inventoryId: string;
  newQuantity: number;
  reasonCode: string;
  notes?: string;
}

export interface GetInventoryMovementsFilters {
  projectId?: string;
  resourceId?: string;
  storageLocationId?: string;
  storageZoneId?: string;
  taskId?: string;
  movementType?: InventoryMovementItem['movementType'];
  sourceType?: InventoryMovementItem['sourceType'];
  createdById?: string;
  from?: string;
  to?: string;
  page?: number;
  limit?: number;
}

type ApiEnvelope<T> = { success?: boolean; data?: T };

const unwrap = <T>(response: any): T => {
  const body = response?.data ?? response;
  const envelope = body as ApiEnvelope<T>;
  return envelope && Object.prototype.hasOwnProperty.call(envelope, 'data')
    ? (envelope.data as T)
    : (body as T);
};

// Backend returns a paginated envelope on this endpoint:
// { success, data: { movements: [...], total, page, limit, totalPages } }
// Older code mis-typed this as a bare array; storing the pagination object
// into state caused `movements.slice` to throw and white-screen the Admin
// Resources tab.
interface PaginatedMovements {
  movements: InventoryMovementItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

class InventoryMovementService {
  async getMovements(filters?: GetInventoryMovementsFilters): Promise<InventoryMovementItem[]> {
    const params = new URLSearchParams();

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value));
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `/inventory-movements?${queryString}` : '/inventory-movements';
    const response = await apiClient.get<ApiEnvelope<PaginatedMovements>>(url);
    const payload = unwrap<PaginatedMovements>(response);
    return payload?.movements ?? [];
  }

  async createMovement(payload: CreateInventoryMovementPayload): Promise<InventoryMovementItem> {
    const response = await apiClient.post<ApiEnvelope<InventoryMovementItem>>('/inventory-movements', payload);
    return unwrap<InventoryMovementItem>(response);
  }

  async createAdjustment(payload: CreateInventoryAdjustmentPayload): Promise<InventoryMovementItem> {
    const response = await apiClient.post<ApiEnvelope<InventoryMovementItem>>(
      '/inventory-movements/adjustment',
      payload
    );
    return unwrap<InventoryMovementItem>(response);
  }
}

export default new InventoryMovementService();
